import React, { useEffect, useMemo, useRef, useState } from "react";

import axios from "axios";

import {
  Search,
  Menu,
  Maximize2,
  ImagePlus,
  Video,
  Camera,
  Film,
  Image as GalleryIcon,
} from "lucide-react";

import "./SiteProgress.css";

const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.quote.softvolute.in";

const progressOptions = [0, 25, 50, 75, 100];

// Which hidden file box each button opens, and what we call the upload

const uploadLabels = {
  photo: "Photo",

  video: "Video",

  camera: "Photo",
};

const SiteProgress = ({ project }) => {
  const [items, setItems] = useState([]);

  const [pendingChanges, setPendingChanges] = useState({});

  const [savingChanges, setSavingChanges] = useState(false);

  const [saveMessage, setSaveMessage] = useState("");

  const [activeTab, setActiveTab] = useState("scope"); // "scope" | "report"

  const [activeRoom, setActiveRoom] = useState("All");

  const [search, setSearch] = useState("");

  const [categoryFilter, setCategoryFilter] = useState("");

  const [statusFilter, setStatusFilter] = useState("");

  const [typeFilter, setTypeFilter] = useState("");

  const [progressFilter, setProgressFilter] = useState("");

  const [loading, setLoading] = useState(false);

  const [selectedUploadItem, setSelectedUploadItem] = useState(null);

  const [selectedItem, setSelectedItem] = useState(null);

  const [photoSheetItem, setPhotoSheetItem] = useState(null);

  const [mediaPreview, setMediaPreview] = useState(null);

  const [uploadingItemId, setUploadingItemId] = useState(null);

  const [removingMediaId, setRemovingMediaId] = useState(null);

  const photoInputRef = useRef(null);

  const videoInputRef = useRef(null);

  const cameraInputRef = useRef(null);

  // The live Camera button is shown on touch screens (phones)

  const isTouch =
    typeof window !== "undefined" &&
    (navigator.maxTouchPoints > 0 || "ontouchstart" in window);

  const loginUser =
    JSON.parse(localStorage.getItem("user"))?.name ||
    localStorage.getItem("name") ||
    "";

  const getFileUrl = (url) => {
    if (!url) return "";
    if (/^https?:\/\//i.test(url)) return url;
    const origin = BASE_URL.replace(/\/api\/?$/, "").replace(/\/$/, "");
    return `${origin}${url.startsWith("/") ? "" : "/"}${url}`;
  };

  const fetchSiteProgress = async () => {
    if (!project?.name) return;

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/site-progress`, {
        params: {
          projectName: project.name,

          projectId: project._id || project.id || "",
        },
      });

      setItems(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch (error) {
      console.error("Site progress fetch error:", error);

      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSiteProgress();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project]);

  useEffect(() => {
    const warn = (event) => {
      if (!Object.keys(pendingChanges).length) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [pendingChanges]);

  const updateItemInState = (updatedItem) => {
    setItems((prev) =>
      prev.map((oldItem) =>
        oldItem._id === updatedItem._id ? updatedItem : oldItem,
      ),
    );

    setSelectedItem((prev) =>
      prev?._id === updatedItem._id ? updatedItem : prev,
    );
  };

  const getDraftProgress = (item) =>
    Object.prototype.hasOwnProperty.call(pendingChanges, item._id)
      ? pendingChanges[item._id]
      : Number(item.progress || 0);

  const handleProgressChange = (item, value) => {
    if (savingChanges) return;
    const next = Number(value);
    if (!Number.isFinite(next) || next < 0 || next > 100) return;
    setSaveMessage("");
    setPendingChanges((previous) => {
      const changes = { ...previous };
      if (next === Number(item.progress || 0)) delete changes[item._id];
      else changes[item._id] = next;
      return changes;
    });
  };

  const handleDrawerSliderChange = (value) => {
    if (!selectedItem) return;
    handleProgressChange(selectedItem, value);
  };

  const cancelChanges = () => {
    if (savingChanges) return;
    setPendingChanges({});
    setSaveMessage("");
  };

  const saveChanges = async () => {
    if (savingChanges || !project?.name) return;
    const entries = Object.entries(pendingChanges);
    if (!entries.length) return;
    setSavingChanges(true);
    setSaveMessage("");
    const failed = {};
    let savedCount = 0;
    const savedItems = [];
    for (const [itemId, progress] of entries) {
      try {
        const response = await axios.put(
          `${BASE_URL}/api/site-progress/${encodeURIComponent(project.name)}/items/${itemId}`,
          { progress, updatedBy: loginUser },
        );
        if (!response.data?.item) throw new Error("Invalid server response");
        savedItems.push(response.data.item);
        savedCount += 1;
      } catch (error) {
        console.error("Progress save error:", error);
        failed[itemId] = progress;
      }
    }
    if (savedItems.length) {
      const updated = new Map(
        savedItems.map((item) => [String(item._id), item]),
      );
      setItems((prev) =>
        prev.map((item) => updated.get(String(item._id)) || item),
      );
      setSelectedItem((prev) =>
        prev ? updated.get(String(prev._id)) || prev : prev,
      );
    }
    setPendingChanges(failed);
    setSaveMessage(
      Object.keys(failed).length
        ? `${savedCount} saved, ${Object.keys(failed).length} failed. Retry Save Changes.`
        : `${savedCount} item${savedCount === 1 ? "" : "s"} saved successfully.`,
    );
    setSavingChanges(false);
  };

  // Uploads every selected file (one by one) to the same upload endpoint.
  const handleFileUpload = async (e, uploadType) => {
    const input = e.target;

    const files = Array.from(input.files || []);

    const targetItem = selectedUploadItem;

    if (!files.length || !targetItem) return;

    let failedCount = 0;

    let lastError = "";

    try {
      setUploadingItemId(targetItem._id);

      for (const file of files) {
        const formData = new FormData();

        formData.append("file", file);

        formData.append("uploadedBy", loginUser);

        try {
          const res = await axios.post(
            `${BASE_URL}/api/site-progress/${encodeURIComponent(
              project.name,
            )}/items/${targetItem._id}/upload`,

            formData,
          );

          if (res.data?.item) updateItemInState(res.data.item);
        } catch (error) {
          console.error("Upload error:", error);

          failedCount += 1;

          lastError = error.response?.data?.message || "Upload failed";
        }
      }

      await fetchSiteProgress();

      if (failedCount) {
        alert(
          `${failedCount} of ${files.length} file${
            files.length === 1 ? "" : "s"
          } failed to upload. ${lastError}`,
        );
      }
    } finally {
      input.value = "";

      setSelectedUploadItem(null);

      setUploadingItemId(null);
    }
  };

  const roomGroups = useMemo(() => {
    const grouped = {};

    items.forEach((item) => {
      const room = item.room || "Unsectioned";

      if (!grouped[room]) grouped[room] = [];

      grouped[room].push(item);
    });

    return grouped;
  }, [items]);

  const roomTabs = useMemo(() => Object.keys(roomGroups), [roomGroups]);

  const filteredItemsByRoom = useMemo(() => {
    const selectedRooms =
      activeRoom === "All" ? Object.keys(roomGroups) : [activeRoom];

    const result = {};

    selectedRooms.forEach((room) => {
      const roomItems = roomGroups[room] || [];

      result[room] = roomItems.filter((item) => {
        const searchText =
          `${item.name} ${item.description} ${item.category} ${item.code}`

            .toLowerCase()

            .trim();

        return (
          searchText.includes(search.toLowerCase()) &&
          (categoryFilter ? item.category === categoryFilter : true) &&
          (statusFilter ? item.status === statusFilter : true) &&
          (typeFilter ? item.itemType === typeFilter : true) &&
          (progressFilter
            ? String(item.progress) === String(progressFilter)
            : true)
        );
      });
    });

    return result;
  }, [
    activeRoom,

    roomGroups,

    search,

    categoryFilter,

    statusFilter,

    typeFilter,

    progressFilter,
  ]);

  const getSectionProgress = (roomItems = []) => {
    if (!roomItems.length) return 0;

    const total = roomItems.reduce(
      (sum, item) => sum + Number(item.progress || 0),

      0,
    );

    return Math.round(total / roomItems.length);
  };

  const getDateTime = (date) => {
    if (!date) return "";

    return new Date(date).toLocaleString("en-IN", {
      day: "2-digit",

      month: "short",

      year: "numeric",

      hour: "2-digit",

      minute: "2-digit",
    });
  };

  const uniqueValues = (key) => {
    return [...new Set(items.map((item) => item[key]).filter(Boolean))];
  };

  // ------------------------------------------------------------------

  // Numbers for the Progress Report tab

  // ------------------------------------------------------------------

  const report = useMemo(() => {
    const total = items.length;

    const percentOf = (item) => Number(item.progress || 0);

    const overall = total
      ? Math.round(
          items.reduce((sum, item) => sum + percentOf(item), 0) / total,
        )
      : 0;

    const completed = items.filter((item) => percentOf(item) >= 100).length;

    const inProgress = items.filter(
      (item) => percentOf(item) > 0 && percentOf(item) < 100,
    ).length;

    const notStarted = total - completed - inProgress;

    const allPhotos = items.flatMap((item) =>
      (item.photos || []).map((photo) => ({
        ...photo,

        itemName: item.name,

        room: item.room,
      })),
    );

    const photoCount = allPhotos.length;

    const videoCount = items.reduce(
      (sum, item) => sum + (item.videos || []).length,

      0,
    );

    const recentPhotos = [...allPhotos]

      .sort((a, b) => new Date(b.uploadedAt || 0) - new Date(a.uploadedAt || 0))

      .slice(0, 8);

    const recentItems = items

      .filter((item) => item.updatedAt)

      .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))

      .slice(0, 6);

    const sections = Object.entries(roomGroups).map(([room, list]) => ({
      room,

      count: list.length,

      done: list.filter((item) => percentOf(item) >= 100).length,

      progress: list.length
        ? Math.round(
            list.reduce((sum, item) => sum + percentOf(item), 0) / list.length,
          )
        : 0,
    }));

    return {
      total,

      overall,

      completed,

      inProgress,

      notStarted,

      photoCount,

      videoCount,

      recentPhotos,

      recentItems,

      sections,
    };
  }, [items, roomGroups]);

  // type: "photo" (gallery), "video" (gallery), "camera" (live photo)

  const openUpload = (item, type) => {
    setSelectedUploadItem(item);

    const refs = {
      photo: photoInputRef,

      video: videoInputRef,

      camera: cameraInputRef,
    };

    refs[type]?.current?.click();
  };

  // The visible upload buttons (used in every row and in the details drawer)

  const renderUploadButtons = (item, extraClass = "") => (
    <div className={`sp-upload-actions ${extraClass}`}>
      <button
        type="button"
        className="sp-upload-btn"
        onClick={(e) => {
          e.stopPropagation();

          // Phone: ask "Camera or Gallery?". Laptop: open the file picker.

          if (isTouch) {
            setPhotoSheetItem(item);
          } else {
            openUpload(item, "photo");
          }
        }}
      >
        <Camera size={16} />

        <span>Photo</span>
      </button>

      <button
        type="button"
        className="sp-upload-btn"
        onClick={(e) => {
          e.stopPropagation();

          openUpload(item, "video");
        }}
      >
        <Film size={16} />

        <span>Video</span>
      </button>
    </div>
  );

  // Remove only the MongoDB reference; backend must NOT delete the physical file.

  const handleRemoveMedia = async (item, media, mediaType) => {
    if (!media?._id || !item?._id) {
      alert("Media ID is missing. Cannot remove this item.");

      return;
    }

    if (
      !window.confirm(
        `Remove this ${mediaType} from Site Progress?\nThe original file will remain on the server.`,
      )
    )
      return;

    try {
      setRemovingMediaId(String(media._id));

      const res = await axios.delete(
        `${BASE_URL}/api/site-progress/${encodeURIComponent(project.name)}/items/${item._id}/media/${mediaType}/${media._id}`,
      );

      if (res.data?.item) updateItemInState(res.data.item);
      else await fetchSiteProgress();

      setMediaPreview((prev) =>
        prev?.url === getFileUrl(media.fileUrl) ? null : prev,
      );
    } catch (error) {
      console.error("Remove media error:", error);

      alert(error.response?.data?.message || "Could not remove media");
    } finally {
      setRemovingMediaId(null);
    }
  };

  const renderMediaRemoveButton = (item, media, type) => (
    <button
      type="button"
      className="sp-media-remove"
      aria-label={`Remove ${type}`}
      title={`Remove ${type} from Site Progress (file stays on server)`}
      disabled={removingMediaId === String(media._id)}
      onClick={(e) => {
        e.preventDefault();

        e.stopPropagation();

        handleRemoveMedia(item, media, type);
      }}
    >
      ×
    </button>
  );

  const renderRowMedia = (item) => {
    const photos = item.photos || [];

    const videos = item.videos || [];

    if (!photos.length && !videos.length && uploadingItemId !== item._id)
      return null;

    return (
      <div className="sp-row-media" onClick={(e) => e.stopPropagation()}>
        {uploadingItemId === item._id && (
          <span className="sp-media-uploading">Uploading...</span>
        )}

        {photos.map((photo) => (
          <div className="sp-row-media-wrap" key={photo._id || photo.fileUrl}>
            <button
              type="button"
              className="sp-row-media-card"
              title={photo.fileName || "Open photo"}
              onClick={() =>
                setMediaPreview({
                  type: "image",

                  url: getFileUrl(photo.fileUrl),
                })
              }
            >
              <img
                src={getFileUrl(photo.fileUrl)}
                alt={photo.fileName || "Site photo"}
                loading="lazy"
              />
            </button>

            {renderMediaRemoveButton(item, photo, "photo")}
          </div>
        ))}

        {videos.map((video) => (
          <div className="sp-row-media-wrap" key={video._id || video.fileUrl}>
            <button
              type="button"
              className="sp-row-media-card"
              title={video.fileName || "Play video"}
              onClick={() =>
                setMediaPreview({
                  type: "video",

                  url: getFileUrl(video.fileUrl),
                })
              }
            >
              <video
                src={getFileUrl(video.fileUrl)}
                preload="metadata"
                muted
                playsInline
              />

              <span className="sp-video-play">▶</span>
            </button>

            {renderMediaRemoveButton(item, video, "video")}
          </div>
        ))}
      </div>
    );
  };

  // ------------------------------------------------------------------

  // Progress Report tab

  // ------------------------------------------------------------------

  const renderReport = () => {
    if (loading) {
      return <div style={styles.empty}>Loading site progress...</div>;
    }

    if (items.length === 0) {
      return (
        <div style={styles.empty}>
          No approved quote items found for this project.
        </div>
      );
    }

    const statCards = [
      { label: "Total items", value: report.total, tone: "navy" },

      { label: "Completed", value: report.completed, tone: "green" },

      { label: "In progress", value: report.inProgress, tone: "amber" },

      { label: "Not started", value: report.notStarted, tone: "grey" },

      { label: "Photos", value: report.photoCount, tone: "teal" },

      { label: "Videos", value: report.videoCount, tone: "purple" },
    ];

    return (
      <div className="sp-report">
        {/* Overall progress */}

        <div className="sp-rep-hero">
          <div
            className="sp-rep-ring"
            style={{ "--sp-pct": `${report.overall}%` }}
            role="img"
            aria-label={`Overall progress ${report.overall} percent`}
          >
            <span>{report.overall}%</span>
          </div>

          <div className="sp-rep-hero-text">
            <div className="sp-rep-eyebrow">PROGRESS REPORT</div>

            <h3>Overall Progress</h3>

            <p>
              {report.completed} of {report.total} items completed
            </p>

            <small>
              {project?.name || "Selected Project"} · as on{" "}
              {new Date().toLocaleDateString("en-IN", {
                day: "2-digit",

                month: "short",

                year: "numeric",
              })}
            </small>
          </div>
        </div>

        {/* Numbers */}

        <div className="sp-rep-stats">
          {statCards.map((card) => (
            <div key={card.label} className={`sp-rep-stat tone-${card.tone}`}>
              <strong>{card.value}</strong>

              <span>{card.label}</span>
            </div>
          ))}
        </div>

        <div className="sp-rep-columns">
          {/* Section-wise progress */}

          <div className="sp-rep-panel">
            <div className="sp-rep-panel-title">Section-wise progress</div>

            {report.sections.map((section) => (
              <div key={section.room} className="sp-rep-section">
                <div className="sp-rep-section-top">
                  <span className="sp-rep-section-name">{section.room}</span>

                  <span className="sp-rep-section-pct">
                    {section.progress}%
                  </span>
                </div>

                <div className="sp-rep-bar">
                  <div
                    className="sp-rep-bar-fill"
                    style={{ width: `${section.progress}%` }}
                  />
                </div>

                <div className="sp-rep-section-meta">
                  {section.done} of {section.count} items completed
                </div>
              </div>
            ))}
          </div>

          {/* Recently updated */}

          <div className="sp-rep-panel">
            <div className="sp-rep-panel-title">Recently updated</div>

            {report.recentItems.length === 0 ? (
              <div className="sp-rep-empty">No updates yet</div>
            ) : (
              report.recentItems.map((item) => (
                <button
                  key={item._id}
                  type="button"
                  className="sp-rep-recent"
                  onClick={() => setSelectedItem(item)}
                >
                  <span className="sp-rep-recent-main">
                    <span className="sp-rep-recent-name">{item.name}</span>

                    <span className="sp-rep-recent-meta">
                      {item.room || "Unsectioned"} ·{" "}
                      {getDateTime(item.updatedAt)}
                    </span>
                  </span>

                  <span className="sp-rep-recent-pct">
                    {Number(item.progress || 0)}%
                  </span>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Latest photos */}

        <div className="sp-rep-panel">
          <div className="sp-rep-panel-title">Latest site photos</div>

          {report.recentPhotos.length === 0 ? (
            <div className="sp-rep-empty">No photos uploaded yet</div>
          ) : (
            <div className="sp-rep-photos">
              {report.recentPhotos.map((photo) => (
                <a
                  key={photo._id || photo.fileUrl}
                  href={getFileUrl(photo.fileUrl)}
                  target="_blank"
                  rel="noreferrer"
                  className="sp-rep-photo"
                >
                  <img
                    src={getFileUrl(photo.fileUrl)}
                    alt={photo.itemName || "site progress"}
                  />

                  <span className="sp-rep-photo-name">{photo.itemName}</span>

                  <span className="sp-rep-photo-date">
                    {getDateTime(photo.uploadedAt)}
                  </span>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="sp-page" style={styles.page}>
      {/* Hidden file boxes. Camera opens the phone camera directly. */}

      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: "none" }}
        onChange={(e) => handleFileUpload(e, uploadLabels.photo)}
      />

      <input
        ref={videoInputRef}
        type="file"
        accept="video/*"
        multiple
        style={{ display: "none" }}
        onChange={(e) => handleFileUpload(e, uploadLabels.video)}
      />

      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: "none" }}
        onChange={(e) => handleFileUpload(e, uploadLabels.camera)}
      />

      <div className="sp-header" style={styles.header}>
        <div>
          <button
            style={styles.backBtn}
            onClick={() => {
              if (
                Object.keys(pendingChanges).length &&
                !window.confirm("Discard unsaved progress changes?")
              )
                return;
              window.history.back();
            }}
          >
            ← Back
          </button>

          <h2 style={styles.title}>Work Progress</h2>

          <p style={styles.projectName}>
            Project: <b>{project?.name || "Selected Project"}</b>
          </p>
        </div>

        <button style={styles.actionBtn}>Actions</button>
      </div>

      <div className="sp-top-tabs" style={styles.topTabs}>
        <button
          type="button"
          onClick={() => setActiveTab("scope")}
          style={{
            ...styles.topTab,

            ...(activeTab === "scope" ? styles.activeTopTab : {}),
          }}
        >
          Scope Update
        </button>

        <button
          type="button"
          onClick={() => {
            if (
              Object.keys(pendingChanges).length &&
              !window.confirm(
                "Unsaved changes will remain pending. Open report?",
              )
            )
              return;
            setActiveTab("report");
          }}
          style={{
            ...styles.topTab,

            ...(activeTab === "report" ? styles.activeTopTab : {}),
          }}
        >
          Progress Report
        </button>
      </div>

      {activeTab === "report" ? (
        renderReport()
      ) : (
        <div className="sp-card" style={styles.card}>
          <div style={styles.roomTabWrapper}>
            <Menu size={18} color="#777" />

            <button
              onClick={() => setActiveRoom("All")}
              style={{
                ...styles.roomTab,

                ...(activeRoom === "All" ? styles.activeRoomTab : {}),
              }}
            >
              All <span style={styles.count}>{items.length}</span>
            </button>

            {roomTabs.map((room) => (
              <button
                key={room}
                onClick={() => setActiveRoom(room)}
                style={{
                  ...styles.roomTab,

                  ...(activeRoom === room ? styles.activeRoomTab : {}),
                }}
              >
                {room}{" "}
                <span style={styles.count}>{roomGroups[room].length}</span>
              </button>
            ))}

            <Maximize2 size={17} color="#333" />
          </div>

          <div className="sp-table-header" style={styles.tableHeader}>
            <div></div>

            <div>S.No</div>

            <div>Item Name & Description</div>

            <div>Category</div>

            <div>Status</div>

            <div>Item Type</div>

            <div>UOM</div>

            <div>Quantity</div>

            <div>Progress</div>
          </div>

          <div className="sp-filter-row" style={styles.filterRow}>
            <div className="sp-f-check">
              <input type="checkbox" />
            </div>

            <div className="sp-f-icon">
              <Search size={17} color="#aaa" />
            </div>

            <div className="sp-f-search">
              <input
                style={styles.input}
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div>
              <select
                style={styles.select}
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                aria-label="Filter by category"
              >
                <option value="">Category</option>

                {uniqueValues("category").map((v) => (
                  <option key={v}>{v}</option>
                ))}
              </select>
            </div>

            <div>
              <select
                style={styles.select}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                aria-label="Filter by status"
              >
                <option value="">Status</option>

                {uniqueValues("status").map((v) => (
                  <option key={v}>{v}</option>
                ))}
              </select>
            </div>

            <div>
              <select
                style={styles.select}
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                aria-label="Filter by item type"
              >
                <option value="">Item Type</option>

                {uniqueValues("itemType").map((v) => (
                  <option key={v}>{v}</option>
                ))}
              </select>
            </div>

            <div className="sp-f-uom">
              <select style={styles.select}>
                <option>Select</option>
              </select>
            </div>

            <div className="sp-f-qty">
              <input style={styles.input} placeholder="Search" />
            </div>

            <div>
              <select
                style={styles.select}
                value={progressFilter}
                onChange={(e) => setProgressFilter(e.target.value)}
                aria-label="Filter by progress"
              >
                <option value="">Progress</option>

                {progressOptions.map((p) => (
                  <option key={p} value={p}>
                    {p}%
                  </option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <div style={styles.empty}>Loading site progress...</div>
          ) : items.length === 0 ? (
            <div style={styles.empty}>
              No approved quote items found for this project.
            </div>
          ) : (
            Object.entries(filteredItemsByRoom).map(([room, roomItems]) => (
              <div key={room}>
                <div className="sp-section-row" style={styles.sectionRow}>
                  <div>
                    {room} <span style={styles.count}>{roomItems.length}</span>
                  </div>

                  <div>Section Progress: {getSectionProgress(roomItems)}%</div>
                </div>

                {roomItems.map((item) => (
                  <div
                    key={item._id}
                    className="sp-item-row"
                    style={styles.itemRow}
                    onClick={() => setSelectedItem(item)}
                  >
                    <div className="sp-c-check">
                      <input
                        type="checkbox"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>

                    <div className="sp-c-sr">
                      <b>{item.srNo}.</b>
                    </div>

                    <div className="sp-c-name">
                      <div style={styles.itemName}>{item.name}</div>

                      <div className="sp-desc" style={styles.desc}>
                        {item.description}
                      </div>
                    </div>

                    <div className="sp-c-cat" data-label="Category">
                      <div>{item.category}</div>

                      <div style={styles.code}>{item.code}</div>
                    </div>

                    <div className="sp-c-status" data-label="Status">
                      <span
                        style={{
                          ...styles.statusBadge,

                          background:
                            item.status === "Completed"
                              ? "#dcfce7"
                              : item.status === "In Progress"
                                ? "#fef9c3"
                                : "#f1f5f9",

                          color:
                            item.status === "Completed"
                              ? "#166534"
                              : item.status === "In Progress"
                                ? "#854d0e"
                                : "#475569",
                        }}
                      >
                        {item.status}
                      </span>
                    </div>

                    <div className="sp-c-type" data-label="Item Type">
                      <span style={styles.typeBadge}>{item.itemType}</span>
                    </div>

                    <div className="sp-c-uom" data-label="UOM">
                      {item.uom}
                    </div>

                    <div className="sp-c-qty" data-label="Quantity">
                      {item.quantity}
                    </div>

                    <div className="sp-c-progress">
                      <div
                        className="sp-progress-top"
                        style={styles.progressTop}
                      >
                        <span>Progress Status: {getDraftProgress(item)}%</span>

                        <select
                          value={getDraftProgress(item)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) =>
                            handleProgressChange(item, e.target.value)
                          }
                          style={styles.progressSelect}
                        >
                          {progressOptions.map((p) => (
                            <option key={p} value={p} disabled={savingChanges}>
                              {p}%
                            </option>
                          ))}
                        </select>

                        <span style={styles.timeText}>
                          {getDateTime(item.updatedAt)}
                        </span>
                      </div>

                      <div style={styles.progressLineWrap}>
                        <div style={styles.progressLineBg}>
                          <div
                            style={{
                              ...styles.progressLine,

                              width: `${getDraftProgress(item)}%`,
                            }}
                          />

                          <div
                            style={{
                              ...styles.progressCircle,

                              left: `calc(${getDraftProgress(item)}% - 8px)`,
                            }}
                          />
                        </div>

                        <div style={styles.scale}>
                          <span>0%</span>

                          <span>25%</span>

                          <span>50%</span>

                          <span>75%</span>

                          <span>100%</span>
                        </div>
                      </div>

                      {/* Photo / Video upload buttons */}

                      {renderUploadButtons(item)}

                      {renderRowMedia(item)}
                    </div>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === "scope" && (
        <div
          className="sp-save-bar"
          style={{
            position: "sticky",
            bottom: 12,
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 10,
            flexWrap: "wrap",
            marginTop: 14,
            padding: "12px 16px",
            background: "#fff",
            border: "1px solid #d7dce3",
            borderRadius: 10,
            boxShadow: "0 5px 20px rgba(0,0,0,.08)",
          }}
        >
          <span
            style={{ marginRight: "auto", fontSize: 13, color: "#475569" }}
            role="status"
          >
            {Object.keys(pendingChanges).length
              ? `${Object.keys(pendingChanges).length} unsaved change(s)`
              : saveMessage || "All progress changes saved"}
          </span>
          {Object.keys(pendingChanges).length > 0 && (
            <button
              type="button"
              onClick={cancelChanges}
              disabled={savingChanges}
              style={{
                padding: "10px 16px",
                borderRadius: 8,
                border: "1px solid #cbd5e1",
                background: "#fff",
                cursor: "pointer",
              }}
            >
              Cancel Changes
            </button>
          )}
          <button
            type="button"
            onClick={saveChanges}
            disabled={savingChanges || Object.keys(pendingChanges).length === 0}
            style={{
              padding: "10px 18px",
              borderRadius: 8,
              border: "none",
              fontWeight: 700,
              background: "#343a76",
              color: "#fff",
              cursor: "pointer",
              opacity:
                savingChanges || !Object.keys(pendingChanges).length ? 0.55 : 1,
            }}
          >
            {savingChanges ? "Saving..." : "Save Changes"}
          </button>
        </div>
      )}

      {selectedItem && (
        <div style={styles.drawerOverlay}>
          <div className="sp-drawer" style={styles.drawer}>
            <button
              type="button"
              style={styles.fixedCloseBtn}
              onClick={() => setSelectedItem(null)}
            >
              ×
            </button>

            <div style={styles.drawerHeader}>
              <h3 style={styles.drawerHeading}>Item Details</h3>
            </div>

            <div className="sp-drawer-section" style={styles.drawerSection}>
              <div style={styles.drawerTitle}>Basic Details</div>

              <div className="sp-detail-top" style={styles.detailTop}>
                <div className="sp-placeholder" style={styles.placeholderBox}>
                  <ImagePlus size={52} color="#aaa" />
                </div>

                <div style={styles.detailInfo}>
                  <label>Element Name</label>

                  <p>{selectedItem.name}</p>

                  <label>Element Description</label>

                  <p>{selectedItem.description || "-"}</p>
                </div>
              </div>

              <div style={styles.detailGrid}>
                <div>
                  <label>Element Category</label>

                  <p>{selectedItem.category || "-"}</p>
                </div>

                <div>
                  <label>Element Code</label>

                  <p>{selectedItem.code || "-"}</p>
                </div>

                <div>
                  <label>UOM</label>

                  <p>{selectedItem.uom || "-"}</p>
                </div>

                <div>
                  <label>Quantity</label>

                  <p>{selectedItem.quantity || "-"}</p>
                </div>

                <div>
                  <label>Item Type</label>

                  <p>{selectedItem.itemType || "-"}</p>
                </div>

                <div>
                  <label>Status</label>

                  <p>{selectedItem.status || "-"}</p>
                </div>
              </div>
            </div>

            <div className="sp-drawer-section" style={styles.drawerSection}>
              <div style={styles.drawerTitle}>Progress</div>

              <div style={styles.methodBox}>
                <p style={styles.smallLabel}>Method of Update</p>

                <label style={styles.radioLabel}>
                  <input type="radio" checked readOnly />
                  Percentage
                </label>

                <label style={styles.radioLabel}>
                  <input type="radio" disabled />
                  Quantity
                </label>
              </div>

              <div style={styles.drawerProgressTop}>
                <span>
                  Progress
                  <br />
                  Status: {getDraftProgress(selectedItem)}%
                </span>

                <span style={styles.timeText}>
                  {getDateTime(selectedItem.updatedAt)}
                </span>
              </div>

              <input
                type="range"
                min="0"
                max="100"
                step="25"
                value={getDraftProgress(selectedItem)}
                onChange={(e) => handleDrawerSliderChange(e.target.value)}
                style={styles.drawerRange}
              />

              <div style={styles.scale}>
                <span>0%</span>

                <span>25%</span>

                <span>50%</span>

                <span>75%</span>

                <span>100%</span>
              </div>

              <div className="sp-media-header" style={styles.mediaHeader}>
                <span>Image / Video</span>
              </div>

              {renderUploadButtons(selectedItem, "sp-upload-actions-drawer")}

              <div className="sp-media-grid" style={styles.mediaGrid}>
                {(selectedItem.photos || []).map((photo) => (
                  <div
                    className="sp-drawer-media-wrap"
                    key={photo._id || photo.fileUrl}
                  >
                    <button
                      type="button"
                      className="sp-drawer-media-open"
                      onClick={() =>
                        setMediaPreview({
                          type: "image",

                          url: getFileUrl(photo.fileUrl),
                        })
                      }
                    >
                      <img
                        src={getFileUrl(photo.fileUrl)}
                        alt="Site progress"
                        style={styles.mediaImg}
                      />

                      <span>{getDateTime(photo.uploadedAt)}</span>
                    </button>

                    {renderMediaRemoveButton(selectedItem, photo, "photo")}
                  </div>
                ))}

                {(selectedItem.videos || []).map((video) => (
                  <div
                    className="sp-drawer-media-wrap"
                    key={video._id || video.fileUrl}
                  >
                    <button
                      type="button"
                      className="sp-drawer-media-open"
                      onClick={() =>
                        setMediaPreview({
                          type: "video",

                          url: getFileUrl(video.fileUrl),
                        })
                      }
                    >
                      <div style={styles.videoThumb}>
                        <Video size={34} />
                      </div>

                      <span>{getDateTime(video.uploadedAt)}</span>
                    </button>

                    {renderMediaRemoveButton(selectedItem, video, "video")}
                  </div>
                ))}

                {(selectedItem.photos || []).length === 0 &&
                  (selectedItem.videos || []).length === 0 && (
                    <div style={styles.noMedia}>No photos/videos uploaded</div>
                  )}
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 10,
                  padding: "14px 0",
                }}
              >
                <button
                  type="button"
                  disabled={
                    savingChanges || !Object.keys(pendingChanges).length
                  }
                  onClick={cancelChanges}
                  style={{
                    padding: "9px 14px",
                    border: "1px solid #cbd5e1",
                    borderRadius: 7,
                    background: "#fff",
                  }}
                >
                  Cancel Changes
                </button>
                <button
                  type="button"
                  disabled={
                    savingChanges || !Object.keys(pendingChanges).length
                  }
                  onClick={saveChanges}
                  style={{
                    padding: "9px 16px",
                    border: 0,
                    borderRadius: 7,
                    background: "#343a76",
                    color: "white",
                    fontWeight: 700,
                  }}
                >
                  {savingChanges ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {mediaPreview && (
        <div
          className="sp-preview-overlay"
          onClick={() => setMediaPreview(null)}
          role="presentation"
        >
          <div
            className="sp-preview-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="sp-preview-close"
              aria-label="Close preview"
              onClick={() => setMediaPreview(null)}
            >
              ×
            </button>

            {mediaPreview.type === "image" ? (
              <img src={mediaPreview.url} alt="Full site progress" />
            ) : (
              <video src={mediaPreview.url} controls autoPlay playsInline />
            )}

            <a href={mediaPreview.url} target="_blank" rel="noreferrer">
              Open in new tab
            </a>
          </div>
        </div>
      )}

      {/* "Camera or Gallery?" menu (phones) */}

      {photoSheetItem && (
        <div
          className="sp-sheet-overlay"
          onClick={() => setPhotoSheetItem(null)}
        >
          <div
            className="sp-sheet"
            role="dialog"
            aria-modal="true"
            aria-label="Add photo"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sp-sheet-handle" />

            <div className="sp-sheet-title">Add photo</div>

            <button
              type="button"
              className="sp-sheet-option"
              onClick={() => {
                const target = photoSheetItem;

                setPhotoSheetItem(null);

                openUpload(target, "camera");
              }}
            >
              <span className="sp-sheet-icon">
                <Camera size={22} />
              </span>

              <span className="sp-sheet-text">
                <strong>Take photo</strong>

                <small>Open the camera</small>
              </span>
            </button>

            <button
              type="button"
              className="sp-sheet-option"
              onClick={() => {
                const target = photoSheetItem;

                setPhotoSheetItem(null);

                openUpload(target, "photo");
              }}
            >
              <span className="sp-sheet-icon">
                <GalleryIcon size={22} />
              </span>

              <span className="sp-sheet-text">
                <strong>Choose from gallery</strong>

                <small>Pick an existing photo</small>
              </span>
            </button>

            <button
              type="button"
              className="sp-sheet-cancel"
              onClick={() => setPhotoSheetItem(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  page: {
    background: "#f5f7fb",

    minHeight: "100vh",

    padding: "16px",

    fontFamily: "Inter, Arial, sans-serif",
  },

  header: {
    background: "#fff",

    borderRadius: "12px",

    padding: "14px 18px",

    marginBottom: "12px",

    display: "flex",

    justifyContent: "space-between",

    alignItems: "center",

    boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
  },

  backBtn: {
    border: "none",

    background: "#eef2ff",

    color: "#334155",

    padding: "8px 14px",

    borderRadius: "8px",

    cursor: "pointer",

    fontWeight: 600,

    marginBottom: "8px",
  },

  title: {
    margin: 0,

    fontSize: "22px",

    color: "#111827",
  },

  projectName: {
    margin: "4px 0 0",

    color: "#64748b",

    fontSize: "14px",
  },

  actionBtn: {
    background: "#ef233c",

    color: "#fff",

    border: "none",

    borderRadius: "8px",

    padding: "10px 18px",

    cursor: "pointer",

    fontWeight: 700,
  },

  topTabs: {
    background: "#fff",

    padding: "0 16px",

    borderRadius: "10px",

    display: "flex",

    gap: "20px",

    marginBottom: "14px",
  },

  topTab: {
    padding: "14px 0",

    border: "none",

    background: "transparent",

    cursor: "pointer",

    fontWeight: 600,

    color: "#1f2937",

    borderBottom: "3px solid transparent",

    whiteSpace: "nowrap",
  },

  activeTopTab: {
    color: "#ef233c",

    borderBottom: "3px solid #ef233c",
  },

  card: {
    background: "#fff",

    borderRadius: "10px",

    border: "1px solid #d7dce3",

    overflow: "hidden",
  },

  roomTabWrapper: {
    display: "flex",

    gap: "14px",

    alignItems: "center",

    padding: "12px 16px",

    borderBottom: "1px solid #d7dce3",

    overflowX: "auto",

    whiteSpace: "nowrap",
  },

  roomTab: {
    border: "none",

    background: "transparent",

    padding: "8px 4px",

    fontWeight: 600,

    cursor: "pointer",

    borderBottom: "2px solid transparent",
  },

  activeRoomTab: {
    color: "#ef233c",

    borderBottom: "2px solid #ef233c",
  },

  count: {
    background: "#c7c9cc",

    color: "#fff",

    borderRadius: "7px",

    padding: "2px 6px",

    fontSize: "12px",

    marginLeft: "4px",
  },

  tableHeader: {
    display: "grid",

    gridTemplateColumns: "40px 60px 2.1fr 1fr 0.8fr 0.9fr 0.7fr 0.8fr 3.2fr",

    alignItems: "center",

    padding: "12px 16px 8px",

    fontSize: "13px",

    fontWeight: 700,
  },

  filterRow: {
    display: "grid",

    gridTemplateColumns: "40px 60px 2.1fr 1fr 0.8fr 0.9fr 0.7fr 0.8fr 3.2fr",

    alignItems: "center",

    padding: "0 16px 12px",

    gap: "8px",

    borderBottom: "1px solid #d7dce3",
  },

  input: {
    width: "100%",

    height: "30px",

    border: "none",

    background: "#f1f4f8",

    borderRadius: "6px",

    padding: "0 10px",

    outline: "none",

    fontSize: "13px",
  },

  select: {
    width: "100%",

    height: "30px",

    border: "none",

    background: "#f1f4f8",

    borderRadius: "6px",

    padding: "0 8px",

    outline: "none",

    fontSize: "13px",
  },

  sectionRow: {
    display: "flex",

    justifyContent: "space-between",

    padding: "13px 16px",

    borderBottom: "1px solid #d7dce3",

    fontWeight: 700,
  },

  itemRow: {
    display: "grid",

    gridTemplateColumns: "40px 60px 2.1fr 1fr 0.8fr 0.9fr 0.7fr 0.8fr 3.2fr",

    alignItems: "center",

    padding: "16px",

    minHeight: "94px",

    borderBottom: "1px solid #d7dce3",

    fontSize: "14px",

    cursor: "pointer",
  },

  itemName: {
    fontWeight: 700,

    color: "#111827",

    marginBottom: "4px",
  },

  desc: {
    color: "#334155",

    fontSize: "12px",

    maxWidth: "330px",

    whiteSpace: "nowrap",

    overflow: "hidden",

    textOverflow: "ellipsis",
  },

  code: {
    fontSize: "12px",

    color: "#111827",

    marginTop: "3px",
  },

  statusBadge: {
    borderRadius: "7px",

    padding: "5px 8px",

    fontSize: "12px",

    fontWeight: 700,
  },

  typeBadge: {
    border: "1px solid #a78bfa",

    color: "#7c3aed",

    background: "#faf5ff",

    borderRadius: "7px",

    padding: "4px 8px",

    fontSize: "12px",
  },

  progressTop: {
    display: "flex",

    alignItems: "center",

    gap: "8px",

    color: "#64748b",

    fontSize: "12px",

    marginBottom: "10px",
  },

  progressSelect: {
    height: "28px",

    border: "1px solid #cbd5e1",

    borderRadius: "6px",

    fontSize: "12px",

    padding: "0 6px",

    outline: "none",

    minWidth: "90px",
  },

  timeText: {
    fontSize: "11px",

    color: "#64748b",

    minWidth: "90px",
  },

  progressLineWrap: {
    width: "100%",
  },

  progressLineBg: {
    position: "relative",

    width: "100%",

    height: "6px",

    background: "#e5e7eb",

    borderRadius: "99px",
  },

  progressLine: {
    height: "6px",

    background: "#67cfd5",

    borderRadius: "99px",
  },

  progressCircle: {
    position: "absolute",

    top: "-5px",

    width: "16px",

    height: "16px",

    borderRadius: "50%",

    border: "2px solid #cbd5e1",

    background: "#fff",
  },

  scale: {
    display: "flex",

    justifyContent: "space-between",

    fontSize: "11px",

    color: "#22b8c3",

    marginTop: "6px",
  },

  empty: {
    padding: "40px",

    textAlign: "center",

    color: "#64748b",

    fontWeight: 600,
  },

  drawerOverlay: {
    position: "fixed",

    inset: 0,

    background: "rgba(0,0,0,0.45)",

    zIndex: 999999,

    display: "flex",

    justifyContent: "flex-end",
  },

  drawer: {
    position: "relative",

    width: "520px",

    maxWidth: "100%",

    height: "100vh",

    background: "#fff",

    overflowY: "auto",

    boxShadow: "-4px 0 20px rgba(0,0,0,0.2)",
  },

  fixedCloseBtn: {
    position: "fixed",

    top: "18px",

    right: "18px",

    width: "40px",

    height: "40px",

    borderRadius: "50%",

    border: "none",

    background: "#111827",

    color: "#ffffff",

    fontSize: "26px",

    lineHeight: "40px",

    fontWeight: 700,

    cursor: "pointer",

    zIndex: 1000000,
  },

  drawerHeader: {
    background: "#fff",

    padding: "18px 22px",

    borderBottom: "1px solid #e5e7eb",
  },

  drawerHeading: {
    margin: 0,

    fontSize: "20px",

    fontWeight: 800,

    color: "#111827",
  },

  drawerSection: {
    padding: "18px 22px",

    borderBottom: "1px solid #e5e7eb",
  },

  drawerTitle: {
    fontWeight: 800,

    marginBottom: "14px",
  },

  detailTop: {
    display: "flex",

    gap: "16px",
  },

  placeholderBox: {
    width: "150px",

    height: "150px",

    background: "#eeeeee",

    borderRadius: "4px",

    display: "flex",

    alignItems: "center",

    justifyContent: "center",

    flexShrink: 0,
  },

  detailInfo: {
    flex: 1,
  },

  detailGrid: {
    display: "grid",

    gridTemplateColumns: "1fr 1fr",

    gap: "12px",

    marginTop: "16px",
  },

  smallLabel: {
    color: "#6b7280",

    fontSize: "13px",
  },

  radioLabel: {
    display: "block",

    margin: "8px 0",

    fontSize: "14px",
  },

  methodBox: {
    marginBottom: "16px",
  },

  drawerProgressTop: {
    display: "flex",

    alignItems: "center",

    justifyContent: "space-between",

    gap: "10px",

    marginBottom: "16px",

    fontSize: "13px",
  },

  drawerRange: {
    width: "100%",

    accentColor: "#67cfd5",

    cursor: "pointer",
  },

  mediaHeader: {
    display: "flex",

    justifyContent: "space-between",

    marginTop: "24px",

    marginBottom: "4px",

    fontSize: "14px",

    fontWeight: 700,
  },

  mediaGrid: {
    display: "grid",

    gridTemplateColumns: "repeat(3, 1fr)",

    gap: "10px",

    marginTop: "14px",
  },

  mediaCard: {
    textDecoration: "none",

    color: "#334155",

    fontSize: "11px",
  },

  mediaImg: {
    width: "100%",

    height: "85px",

    objectFit: "cover",

    borderRadius: "6px",

    background: "#eee",
  },

  videoThumb: {
    width: "100%",

    height: "85px",

    borderRadius: "6px",

    background: "#e5e7eb",

    display: "flex",

    justifyContent: "center",

    alignItems: "center",

    color: "#475569",
  },

  noMedia: {
    gridColumn: "1 / -1",

    padding: "20px",

    background: "#f8fafc",

    color: "#64748b",

    textAlign: "center",

    borderRadius: "8px",
  },
};

export default SiteProgress;
